// /admin/roles — Roles list (v6 mockup, mockups/access-preview-v6.html
// data-view="roles"). Compact table that surfaces "what can this role do"
// and "how many people lean on it" so the operator can decide whether a
// role is safe to retire before clicking through.
//
// Visuals mirror the People page (v6-page-header + v6-filter-row +
// v6-table). All rows are clickable; the right-most "Open →" column is
// purely affordance.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { Modal, useToast } from "@/components";
import { roleClient } from "@/data/clients/roleClient";
import { permissionClient } from "@/data/clients/permissionClient";
import { AdminApiError, classifyAdminError } from "@/data/httpClient";
import { roleClass } from "@/pages/admin/people/format";
import { useRole } from "@/role";
import type { PermissionCatalog, Role } from "@/types/role";
import "../AdminShared.css";

interface RoleListItem extends Role {
  users_count?: number;
  rules_count?: number;
}

/** Single source of truth for the row-action delete-disabled rule. */
export function isDeleteDisabled(role: RoleListItem): boolean {
  return (
    role.is_system ||
    (role.users_count ?? 0) > 0 ||
    (role.rules_count ?? 0) > 0
  );
}

/** Tooltip explaining why delete is blocked, or undefined when enabled. */
export function deleteBlockerTooltip(role: RoleListItem): string | undefined {
  if (role.is_system) return "System roles cannot be deleted.";
  if ((role.users_count ?? 0) > 0) {
    return `${role.users_count} user(s) still assigned.`;
  }
  if ((role.rules_count ?? 0) > 0) {
    return `${role.rules_count} rule(s) still reference this role.`;
  }
  return undefined;
}

type TypeFilter = "" | "system" | "custom";
type UsageFilter = "" | "in_use" | "unused";

/** Total permission count across the catalog (denominator for "X of Y"). */
function totalPermissionCount(catalog: PermissionCatalog | null): number {
  if (!catalog) return 0;
  let n = 0;
  for (const g of catalog.groups) n += g.permissions.length;
  return n;
}

/** Plain-English summary of a role's permission set. Cheap & readable —
 *  callers only need a hint, not an exhaustive enumeration. */
function permissionSummary(
  permissionSet: string[],
  total: number,
): string {
  if (total > 0 && permissionSet.length === total) return "all permissions";
  const set = new Set(permissionSet);
  const hasLive = set.has("camera:view_live");
  const hasRecordings = set.has("recording:view");
  if (hasLive && hasRecordings && set.has("recording:export")) {
    return "live + recordings + export";
  }
  if (hasLive && hasRecordings) return "view live + view recordings";
  if (hasLive) return "view live cameras";
  if (hasRecordings) return "view recordings";
  if (permissionSet.length === 0) return "no permissions";
  return `${permissionSet.length} permission${permissionSet.length === 1 ? "" : "s"}`;
}

export function RolesListPage(): JSX.Element {
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();
  const { currentRole } = useRole();

  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [usageFilter, setUsageFilter] = useState<UsageFilter>("");

  const [deleteTarget, setDeleteTarget] = useState<RoleListItem | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [inUseTarget, setInUseTarget] = useState<{
    role: RoleListItem;
    users_count: number;
    rules_count: number;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesResp, cat] = await Promise.all([
        roleClient.list({ limit: 200 }),
        permissionClient.getCatalog().catch(() => null),
      ]);
      setRoles(rolesResp.items as RoleListItem[]);
      setCatalog(cat);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load roles" });
      }
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    if (currentRole === null) return;
    void load();
  }, [currentRole, load]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    const role = deleteTarget;
    try {
      await roleClient.delete(role.role_id, role.version);
      toast.success({ title: `Deleted role "${role.display_name}"` });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteTarget(null);
      const typed =
        err instanceof AdminApiError
          ? err
          : err instanceof ApiError
            ? classifyAdminError(err)
            : null;
      if (typed && typed.typed.kind === "role_in_use") {
        setInUseTarget({
          role,
          users_count: typed.typed.users_count,
          rules_count: typed.typed.rules_count,
        });
      } else if (typed && typed.typed.kind === "version_mismatch") {
        toast.warning({
          title: "Version conflict",
          description:
            "Someone else edited this role. Please reload and try again.",
        });
      } else if (err instanceof ApiError) {
        handleApiError(err, {
          actionLabel: `Delete role ${role.display_name}`,
        });
      }
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteTarget, toast, load, handleApiError]);

  const totalPerms = useMemo(() => totalPermissionCount(catalog), [catalog]);

  // Hide tenant_admin from the list (LLD 11 §6.1: install-bootstrap grant,
  // never editable / never referenced by rules).
  const visibleRoles = useMemo(
    () => roles.filter((r) => r.role_id !== "tenant_admin"),
    [roles],
  );

  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visibleRoles.filter((r) => {
      if (typeFilter === "system" && !r.is_system) return false;
      if (typeFilter === "custom" && r.is_system) return false;
      const usersCount = r.users_count ?? 0;
      if (usageFilter === "in_use" && usersCount === 0) return false;
      if (usageFilter === "unused" && usersCount > 0) return false;
      if (q.length === 0) return true;
      return (
        r.display_name.toLowerCase().includes(q) ||
        r.role_id.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q)
      );
    });
  }, [visibleRoles, search, typeFilter, usageFilter]);

  return (
    <section className="admin-page">
      <header className="v6-page-header">
        <div className="row">
          <h1>Roles</h1>
          <div className="actions">
            <button
              type="button"
              className="v6-btn primary"
              onClick={() => navigate("/admin/roles/new")}
            >
              + New custom role
            </button>
          </div>
        </div>
        <div className="caption">
          A role is what someone is allowed to do. Most teams use the system
          templates; create a custom one only when you need a permission
          combination none of them cover.
        </div>
      </header>

      <div className="v6-filter-row">
        <div className="search-wrap">
          <span className="ico" aria-hidden="true">⌕</span>
          <input
            type="search"
            placeholder="Search roles by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search roles"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          aria-label="Filter by role type"
        >
          <option value="">Type: any</option>
          <option value="system">System</option>
          <option value="custom">Custom</option>
        </select>
        <select
          value={usageFilter}
          onChange={(e) => setUsageFilter(e.target.value as UsageFilter)}
          aria-label="Filter by usage"
        >
          <option value="">Usage: any</option>
          <option value="in_use">In use</option>
          <option value="unused">Unused</option>
        </select>
        <span className="count">
          {loading
            ? "Loading…"
            : `${filteredRoles.length} role${filteredRoles.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <table className="v6-table roles-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Permissions</th>
            <th>Type</th>
            <th>In use by</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows columns={5} />
          ) : filteredRoles.length === 0 ? (
            <tr>
              <td colSpan={5} className="admin-muted" style={{ padding: 24 }}>
                {visibleRoles.length === 0
                  ? "No roles defined yet."
                  : "No roles match your filters."}
              </td>
            </tr>
          ) : (
            filteredRoles.map((r) => (
              <RoleRow
                key={r.role_id}
                role={r}
                totalPermissions={totalPerms}
                onOpen={() =>
                  navigate(`/admin/roles/${encodeURIComponent(r.role_id)}`)
                }
                onDelete={() => setDeleteTarget(r)}
              />
            ))
          )}
        </tbody>
      </table>

      <Modal
        open={deleteTarget !== null}
        onClose={() => {
          if (!deleteSubmitting) setDeleteTarget(null);
        }}
        variant="confirm"
        title={
          deleteTarget
            ? `Delete role "${deleteTarget.display_name}"?`
            : ""
        }
        description={
          deleteTarget
            ? `This removes the role and any membership row that grants it. This cannot be undone.`
            : ""
        }
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="v6-btn ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="v6-btn danger"
              onClick={() => void handleDelete()}
              disabled={deleteSubmitting}
            >
              {deleteSubmitting ? "Deleting…" : "Delete"}
            </button>
          </div>
        }
      />

      <Modal
        open={inUseTarget !== null}
        onClose={() => setInUseTarget(null)}
        variant="confirm"
        title={
          inUseTarget
            ? `Cannot delete "${inUseTarget.role.display_name}"`
            : ""
        }
        description={
          inUseTarget
            ? `${inUseTarget.users_count} user(s) and ${inUseTarget.rules_count} rule(s) still use this role. Reassign or remove them first.`
            : ""
        }
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="v6-btn ghost"
              onClick={() => setInUseTarget(null)}
            >
              Close
            </button>
          </div>
        }
      />
    </section>
  );
}

interface RoleRowProps {
  role: RoleListItem;
  totalPermissions: number;
  onOpen: () => void;
  onDelete: () => void;
}

function RoleRow({
  role,
  totalPermissions,
  onOpen,
  onDelete,
}: RoleRowProps): JSX.Element {
  const usersCount = role.users_count ?? 0;
  const rulesCount = role.rules_count ?? 0;
  const held = role.permission_set.length;
  const denom = totalPermissions > 0 ? totalPermissions : held;
  const summary = permissionSummary(role.permission_set, totalPermissions);
  const deleteDisabled = isDeleteDisabled(role);
  const deleteTooltip = deleteBlockerTooltip(role);

  const stop = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
  };

  return (
    <tr className="clickable" onClick={onOpen}>
      <td className="alias">
        <span className={`v6-role-chip ${roleClass(role.role_id)}`}>
          {role.role_id.toUpperCase()}
        </span>
        <div className="alias-stack">
          <b>{role.display_name}</b>
          <span className="alias-id">{role.description || "—"}</span>
        </div>
      </td>
      <td>
        <span className="v6-perm-count">
          {held} of {denom}
        </span>
        <span
          style={{
            fontSize: 12,
            color: "var(--ring-neutral-300)",
            marginLeft: 8,
          }}
        >
          {summary}
        </span>
      </td>
      <td>
        <span className={`v6-type-chip ${role.is_system ? "system" : "custom"}`}>
          {role.is_system ? "System" : "Custom"}
        </span>
      </td>
      <td>
        <b>
          {usersCount} user{usersCount === 1 ? "" : "s"}
        </b>
        {rulesCount > 0 && (
          <span
            style={{
              fontSize: 12,
              color: "var(--ring-neutral-300)",
              marginLeft: 6,
            }}
          >
            via {rulesCount} rule{rulesCount === 1 ? "" : "s"}
          </span>
        )}
        {usersCount === 0 && (
          <span className="alias-warn" style={{ marginLeft: 6 }}>
            unused
          </span>
        )}
      </td>
      <td className="row-actions">
        <button
          type="button"
          className="v6-btn ghost xs"
          disabled={deleteDisabled}
          title={deleteTooltip}
          aria-label={`Delete ${role.display_name}`}
          onClick={(e) => {
            stop(e);
            onDelete();
          }}
        >
          Delete
        </button>
        <span style={{ fontSize: 12, color: "var(--ring-neutral-300)" }}>
          Open →
        </span>
      </td>
    </tr>
  );
}

function SkeletonRows({ columns }: { columns: number }): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <td key={c}>
              <div
                style={{
                  height: 14,
                  background: "var(--ring-tertiary-bg)",
                  borderRadius: 4,
                  width: c === 0 ? "60%" : c === columns - 1 ? "30%" : "40%",
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
